import React, { useContext } from "react";
import {
  Button,
  HelpDrawerToggle,
  HelpDrawer,
  CloseIconThin,
} from "@cmsgov/ds-medicare-gov";
import { FormattedMessage } from "react-intl-phraseapp";
import { EnrollmentDownAlert, LoadingMask } from "../../components";
import {
  getOppositePlanType,
  useTranslate,
  isMedicareAdvantageType,
} from "../../helpers";
import { AppStore } from "../../app/store";
import {
  SearchResultsState,
  useSearchResultsHelper,
} from "./useSearchResultsHelper";
import { PlanType, ActionType } from "../../@types";
import { useHistory } from "react-router";
import { interpolate } from "../../helpers/translationHelpers";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../app/contexts/Analytics/types";

interface SRPHeaderProps {
  pageState: SearchResultsState;
  updatePageState: (x: Partial<SearchResultsState>) => void;
}

const SearchResultsPageHeader: React.FC<SRPHeaderProps> = ({
  pageState,
  updatePageState,
}) => {
  const { state, dispatch } = useContext(AppStore.AppContext);
  const history = useHistory();
  const { clearPlansToCompare } = useSearchResultsHelper(
    state,
    dispatch,
    pageState
  );

  const t = useTranslate();

  const isMA = isMedicareAdvantageType(state.planType || PlanType.MAPD);

  const switchPlanType = (buttonText: string): void => {
    const oppositePlanType = getOppositePlanType(
      state.planType || PlanType.MAPD
    );

    const switchLink = `/search-results?plan_type=${oppositePlanType.toLowerCase()}&fips=${
      state.fips
    }`;

    clearPlansToCompare();
    updatePageState({ currentPage: 0 });

    history.push(switchLink);
    dispatch({
      type: ActionType.UPDATE_PLAN_TYPE,
      payload: oppositePlanType,
    });

    const tealiumEventLabel = !isMA
      ? "View Available Medicare Advantage Plans"
      : "View Available Drug Plans";
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "Find Plans - Available Plans",
        event_label: tealiumEventLabel,
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
      settings: {
        button: {
          buttonStyle: AnalyticsButtonStyle.DEFAULT,
          buttonType: AnalyticsButtonType.BUTTON,
          text: buttonText,
        },
        linkUrl: switchLink,
      },
    });
  };

  return (
    <>
      <EnrollmentDownAlert className="ds-u-margin-y--3 ds-u-margin-x--7" />
      <div
        className="SearchResults__tellMeMore mct-hide-print"
        data-testid="search-results"
      >
        <FormattedMessage
          id="search_results_page.escape_hatch.other_plans"
          values={{
            pluralPlanType: isMA
              ? t("search_results_page.escape_hatch.separate_drug_plans")
              : t("search_results_page.escape_hatch.medicare_plans"),
            helpDrawerToggleLink: (
              <HelpDrawerToggle
                helpDrawerOpen={pageState.showSwitchPlansHelp}
                showDrawer={(): void => {
                  if (!pageState.showSwitchPlansHelp) {
                    dispatch({
                      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                      settings: {
                        event_action: "Help Drawer",
                        event_label: "Tell Me More - Plan Results",
                      },
                    });
                  }
                  updatePageState({ showSwitchPlansHelp: true });
                }}
                inline
              >
                {t("search_results_page.escape_hatch.tell_me_more")}
              </HelpDrawerToggle>
            ),
          }}
        />

        <LoadingMask loading={pageState.loading} delay={500} />

        {pageState.showSwitchPlansHelp && (
          <div className="mct-c-help-drawer">
            <HelpDrawer
              heading={
                <FormattedMessage
                  id="search_results_page.drawer.title"
                  tagName="span"
                  values={{
                    pluralPlanType: isMA
                      ? t("search_results_page.escape_hatch.drug_plans")
                      : t("search_results_page.escape_hatch.medicare_plans"),
                  }}
                />
              }
              onCloseClick={(): void =>
                updatePageState({ showSwitchPlansHelp: false })
              }
              closeButtonText={<CloseIconThin />}
            >
              <h4 className="ds-u-margin-top--0">
                <FormattedMessage
                  id="search_results_page.drawer.subtitle"
                  tagName="span"
                  values={{
                    pluralPlanType: isMA
                      ? t("search_results_page.escape_hatch.drug_plans")
                      : t("search_results_page.escape_hatch.medicare_plans"),
                    count: pageState.otherPlansAvailable,
                  }}
                />
              </h4>

              {isMA && <p>{t("search_results_page.drawer.switch_to_pdp")}</p>}

              {!isMA && <p>{t("search_results_page.drawer.switch_to_mapd")}</p>}
              <p>{t("search_results.page.drawer.would_you_like_to_switch")}</p>

              <Button
                onClick={(): void => {
                  dispatch({
                    type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                    settings: {
                      event_action: "Help Drawer",
                      event_label: "Tell Me More - Plan Results - Yes",
                    },
                  });
                  updatePageState({ showSwitchPlansHelp: false });
                  switchPlanType(t("yes"));
                }}
                className="ds-u-margin-right--2"
              >
                {t("yes")}
              </Button>

              <Button
                onClick={(): void => {
                  dispatch({
                    type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                    settings: {
                      event_action: "Help Drawer",
                      event_label: "Tell Me More - Plan Results - No",
                    },
                  });
                  dispatch({
                    type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                    settings: {
                      button: {
                        buttonStyle: AnalyticsButtonStyle.DEFAULT,
                        buttonType: AnalyticsButtonType.BUTTON,
                        text: t("no"),
                      },
                    },
                  });
                  updatePageState({ showSwitchPlansHelp: false });
                }}
              >
                {t("no")}
              </Button>
            </HelpDrawer>
          </div>
        )}

        <Button
          variation="ghost"
          size="small"
          className="ds-u-padding--0 ds-u-text-align--right e2e-switch-plan-type"
          onClick={() =>
            switchPlanType(
              interpolate(
                t("search_results_page.escape_hatch.switch_plans"),
                isMA
                  ? t("search_results_page.escape_hatch.drug_plans")
                  : t("search_results_page.escape_hatch.medicare_plans"),
                pageState.otherPlansAvailable
              )
            )
          }
        >
          <FormattedMessage
            id="search_results_page.escape_hatch.switch_plans"
            tagName="span"
            values={{
              pluralPlanType: isMA
                ? t("search_results_page.escape_hatch.drug_plans")
                : t("search_results_page.escape_hatch.medicare_plans"),
              count: pageState.otherPlansAvailable,
            }}
          />
        </Button>
      </div>
    </>
  );
};

export default SearchResultsPageHeader;
